import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Form } from "@/components/ui/form";
import { SubmitButton } from "@/components/ui/submit-button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useFormSubmit } from "@/hooks/useFormSubmit";
import { useTabErrorNavigation } from "@/hooks/useTabErrorNavigation";
import { cn, errorHandler, isValidCpf } from "@/lib/utils";
import { driverService } from "@/services/api/driver";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import {
  Car,
  ChevronLeft,
  ChevronRight,
  User as DriverIcon,
  Settings,
} from "lucide-react";
import { useEffect, useState } from "react";
import {
  SubmitErrorHandler,
  useFieldArray,
  useForm,
  type Resolver,
} from "react-hook-form";
import { useNavigate, useParams } from "react-router";
import { z } from "zod";
import DriverDataTab from "./DriverDataTab";
import DriverFormSkeleton from "./DriverFormSkeleton";
import DriverModulesTab from "./DriverModulesTab";
import DriverVehiclesTab from "./DriverVehiclesTab";

// Form validation schema
const formSchema = z.object({
  // Driver Data
  driver_data: z.object({
    name: z.string().min(2, "Nome deve ter pelo menos 2 caracteres"),
    // Conveniência para o usuário: a autoridade (inclusive a duplicidade) é o backend.
    cpf: z
      .string()
      .nullable()
      .refine((value) => !value || isValidCpf(value), "CPF inválido"),
    user: z.string().nullable(),
    password: z
      .string()
      .min(6, "Senha deve ter pelo menos 6 caracteres")
      .nullable(),
    phone: z.string().nullable(),
    status: z.number().min(0).max(1),
    serial: z.string().nullable(),
    ibutton: z.string().nullable(),
    enable_notifications: z.boolean().default(false),
    type: z.number().min(0).max(3),
    drivers_license: z.string().nullable(),
    drivers_license_expiration: z.string().date("Data inválida").nullable(),
    drivers_license_category: z.number().nullable(),
    toxicological_exam_date: z.string().date("Data inválida").nullable(),
    medical_exam_date: z.string().date("Data inválida").nullable(),
  }),
  // Vehicles
  vehicles: z
    .array(
      z.object({
        vehicle_id: z.number(),
        permission: z.number(),
        title: z.string().nullable(),
        plate: z.string().nullable(),
        authorization_to_drive: z.number(),
        authorization_to_drive_label: z.string(),
      }),
    )
    .optional(),
  // Modules
  modules: z
    .array(
      z.object({
        module_id: z.number(),
        permission: z.number(),
        module_title: z.string(),
        parent_id: z.number().nullable(),
      }),
    )
    .optional(),
  permissionsToDrive: z
    .array(
      z.object({
        status: z.number(),
        vehicle_id: z.number(),
      }),
    )
    .optional(),
});

export type DriverFormValues = z.infer<typeof formSchema>;

const STEPS = [
  {
    id: "driver_data",
    label: "Dados do usuário",
    icon: DriverIcon,
    priority: 1,
  },
  {
    id: "vehicles",
    label: "Veículos",
    icon: Car,
    priority: 2,
  },
  {
    id: "modules",
    label: "Módulos",
    icon: Settings,
    priority: 3,
  },
];

const TAB_FIELDS_MAPPING = {
  driver_data: ["driver_data"] as const,
  vehicles: ["vehicles", "permissionsToDrive"] as const,
  modules: ["modules"] as const,
} as const;

type StepId = (typeof STEPS)[number]["id"];

const loadDriverValues = async (id: number): Promise<DriverFormValues> => {
  const [
    driverResponse,
    vehiclePermissionsResponse,
    modulePermissionsResponse,
  ] = await Promise.all([
    driverService.show(id),
    driverService.getVehiclePermissions(id),
    driverService.getModulePermissions(id, 1),
  ]);

  const vehicleData = Object.entries(vehiclePermissionsResponse.data).map(
    ([key, value]) => ({
      vehicle_id: Number(key),
      ...value,
    }),
  );

  const permissionsToDrive = vehicleData.map((vehicle) => ({
    status: vehicle.authorization_to_drive,
    vehicle_id: vehicle.vehicle_id,
  }));

  const moduleData = Object.entries(modulePermissionsResponse.data)
    .map(([key, value]) => ({
      module_id: Number(key),
      ...value,
    }))
    .sort((a, b) => a.module_title.localeCompare(b.module_title));

  return {
    driver_data: {
      name: driverResponse.data.name,
      cpf: driverResponse.data.cpf ?? null,
      user: driverResponse.data.user,
      password: null,
      phone: driverResponse.data.phone,
      status: driverResponse.data.status,
      serial: driverResponse.data.ibutton ?? driverResponse.data.serial,
      ibutton: driverResponse.data.ibutton,
      enable_notifications: Boolean(driverResponse.data.enable_notifications),
      type: driverResponse.data.type,
      drivers_license: driverResponse.data.drivers_license,
      drivers_license_expiration:
        driverResponse.data.drivers_license_expiration ?? null,
      drivers_license_category: driverResponse.data.drivers_license_category,
      toxicological_exam_date:
        driverResponse.data.toxicological_exam_date ?? null,
      medical_exam_date: driverResponse.data.medical_exam_date ?? null,
    },
    vehicles: vehicleData,
    modules: moduleData,
    permissionsToDrive: permissionsToDrive,
  };
};

const loadCreateValues = async (): Promise<DriverFormValues> => {
  const [responseVehicles, responseModules] = await Promise.all([
    driverService.getVehicles(0, { platform_id: 1, summarized: true }),
    driverService.getModules(0, 1),
  ]);

  const vehicleData = responseVehicles.data.map((vehicle) => ({
    vehicle_id: vehicle.id,
    permission: 0,
    title: vehicle.title,
    plate: vehicle.plate,
    authorization_to_drive: 0,
    authorization_to_drive_label: "",
  }));

  const moduleData = responseModules.data.map((module) => ({
    module_id: module.id,
    permission: 0,
    module_title: module.name,
    parent_id: module.parent_id,
  }));

  const permissionsToDrive = responseVehicles.data.map((vehicle) => {
    return {
      status: 0,
      vehicle_id: vehicle.id,
    };
  });

  return {
    driver_data: {
      name: "",
      cpf: null,
      user: null,
      password: null,
      phone: null,
      status: 1,
      serial: null,
      ibutton: null,
      enable_notifications: false,
      type: 1,
      drivers_license: null,
      drivers_license_expiration: null,
      drivers_license_category: null,
      toxicological_exam_date: null,
      medical_exam_date: null,
    },
    vehicles: vehicleData,
    modules: moduleData,
    permissionsToDrive,
  };
};

interface DriverFormContentProps {
  id?: string;
  initialValues: DriverFormValues;
}

const DriverFormContent = ({ id, initialValues }: DriverFormContentProps) => {
  const [isIbutton, setIsIbutton] = useState(
    Boolean(initialValues.driver_data.ibutton),
  );
  const [currentStep, setCurrentStep] = useState<StepId>("driver_data");
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const form = useForm<DriverFormValues>({
    // ponytail: resolvers 5 types the raw input apart from the parsed
    // output; the form keeps the parsed type, as it did on resolvers 3.
    resolver: zodResolver(formSchema) as Resolver<DriverFormValues>,
    shouldFocusError: true,
    defaultValues: initialValues,
  });

  // Hook para navegação de erros entre abas
  const { navigateToFirstTab, tabHasErrors } =
    useTabErrorNavigation<DriverFormValues>({
      errors: form.formState.errors,
      tabFieldsMapping: TAB_FIELDS_MAPPING,
      currentTab: currentStep,
      setCurrentTab: setCurrentStep,
      tabs: STEPS,
    });

  const { fields: vehicleFields } = useFieldArray({
    control: form.control,
    name: "vehicles",
  });

  const { fields: moduleFields } = useFieldArray({
    control: form.control,
    name: "modules",
  });

  // Use custom hook for form submission
  const { handleSubmit, loading } = useFormSubmit({
    id,
    createService: driverService.create,
    updateService: driverService.update,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["drivers"] });
      navigate("/drivers");
    },
    onError: (err) => {
      // Duplicidade de CPF só o backend sabe: leva o 422 para o próprio campo.
      const cpfError = axios.isAxiosError(err)
        ? err.response?.data?.data?.failures?.cpf?.[0]
        : undefined;
      if (cpfError) {
        form.setError("driver_data.cpf", { message: cpfError });
        setCurrentStep("driver_data");
      }
    },
  });

  // Form submission handler
  const onSubmit = async (values: DriverFormValues) => {
    try {
      await handleSubmit({
        ...values,
        driver_data: {
          ...values.driver_data,
          serial: values.driver_data.ibutton ? null : values.driver_data.serial,
          enable_notifications: values.driver_data.enable_notifications ? 1 : 0,
        },
      });
    } catch (err) {
      errorHandler(err, "Erro ao salvar usuário");

      const firstTab = navigateToFirstTab();
      if (firstTab && firstTab !== currentStep) {
        setTimeout(() => setCurrentStep(firstTab), 100);
      }
    }
  };

  const currentStepIndex = STEPS.findIndex((step) => step.id === currentStep);
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === STEPS.length - 1;

  const goToNextStep = () => {
    if (!isLastStep) {
      const nextStep = STEPS[currentStepIndex + 1];
      setCurrentStep(nextStep.id);
    }
  };

  const goToPreviousStep = () => {
    if (!isFirstStep) {
      const previousStep = STEPS[currentStepIndex - 1];
      setCurrentStep(previousStep.id);
    }
  };

  const handleStepChange = (value: string) => {
    setCurrentStep(value as StepId);
  };

  const onError: SubmitErrorHandler<DriverFormValues> = () => {
    const firstTab = navigateToFirstTab();
    if (firstTab) {
      setCurrentStep(firstTab);
    }
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit, onError)}
        className="space-y-8"
      >
        <Tabs
          value={currentStep}
          onValueChange={handleStepChange}
          defaultValue="driver_data"
          className="w-full"
        >
          <TabsList className="grid w-full grid-cols-3">
            {STEPS.map((step, index) => (
              <TabsTrigger
                key={step.id}
                value={step.id}
                className={cn(
                  "flex items-center space-x-2 relative",
                  index < currentStepIndex && "text-primary",
                  index === currentStepIndex &&
                    "bg-primary text-primary-foreground",
                  tabHasErrors(step.id) && "ring-2 ring-red-500 ring-offset-2",
                )}
              >
                <step.icon className="w-4 h-4" />
                <span>{step.label}</span>

                {/* Indicador visual de erro */}
                {tabHasErrors(step.id) && (
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-white" />
                )}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* Driver Data Tab */}
          <DriverDataTab
            form={form}
            isIbutton={isIbutton}
            setIsIbutton={setIsIbutton}
          />

          {/* Vehicles Tab */}
          <DriverVehiclesTab fields={vehicleFields} form={form} />

          {/* Modules Tab */}
          <DriverModulesTab fields={moduleFields} form={form} />
        </Tabs>

        <div className="flex justify-between pt-8 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={goToPreviousStep}
            disabled={isFirstStep}
            className="flex items-center space-x-2"
          >
            <ChevronLeft className="w-4 h-4" />
            <span>Anterior</span>
          </Button>

          <div className="flex space-x-2">
            <Button
              type="button"
              variant="destructive"
              onClick={() => navigate("/drivers")}
            >
              Cancelar
            </Button>

            {!isLastStep && (
              <Button
                type="button"
                variant="outline"
                onClick={goToNextStep}
                className="flex items-center space-x-2"
              >
                <span>Próximo</span>
                <ChevronRight className="w-4 h-4" />
              </Button>
            )}

            <SubmitButton loading={loading} />
          </div>
        </div>
      </form>
    </Form>
  );
};

const DriverForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: initialValues, error } = useQuery({
    queryKey: ["drivers", id ? Number(id) : "create"],
    queryFn: () => (id ? loadDriverValues(Number(id)) : loadCreateValues()),
    // O formulário só lê os valores ao montar; sem cache, a edição sempre abre
    // com o dado atual em vez de um snapshot de minutos atrás.
    gcTime: 0,
  });

  useEffect(() => {
    // Só a carga inicial redireciona: uma refetch em background que falhe (ex.:
    // ao reconectar) não pode tirar o usuário do formulário no meio da edição.
    if (error && !initialValues) {
      errorHandler(error, "Erro ao carregar usuário");
      navigate("/drivers");
    }
  }, [error, initialValues, navigate]);

  return (
    <Card className="mx-auto">
      <CardHeader>
        <CardTitle>{id ? "Editar Motorista" : "Novo Motorista"}</CardTitle>
      </CardHeader>
      <CardContent>
        {initialValues ? (
          <DriverFormContent
            key={id ?? "create"}
            id={id}
            initialValues={initialValues}
          />
        ) : (
          <DriverFormSkeleton />
        )}
      </CardContent>
    </Card>
  );
};

export default DriverForm;
